import { Box, Button, Flex, Input, Text, Divider, IconButton } from '@chakra-ui/react'
import { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react'

import { ConfirmDialog } from '../../../../components/ConfirmDialog'
import { FormField } from '../../../../components/FormField'
import { FormModal } from '../../../../components/FormModal'
import { Icon } from '../../../../components/Icon'
import { SearchableSelect } from '../../../../components/Select'
import { toFormErrors, useHasRole, useAppToast } from '../../../../helpers'
import UrlShortenerService, {
  ICreateUrlShortenerPayload,
  IListUtms,
  IUrlShortener
} from '../../../../services/UrlShortener/UrlShortenerService'

interface IUtmGeneratorFormModalProps {
  isOpen: boolean
  onClose: () => void
  onSaved: () => void
  used_by: 'marketing' | 'commercial'
  initialData?: Partial<IUrlShortener>
  editing?: IUrlShortener | null
}

function formatUrlWithUTMs(form: ICreateUrlShortenerPayload): string | JSX.Element {
  try {
    if (form.original_url === '') {
      return (
        <Text as="span" color="gray.500">
          URL não informada
        </Text>
      )
    }

    const url = new URL(form.original_url)
    if (form.utm_campaign) url.searchParams.set('utm_campaign', form.utm_campaign)
    if (form.utm_source) url.searchParams.set('utm_source', form.utm_source)
    if (form.utm_medium) url.searchParams.set('utm_medium', form.utm_medium)
    if (form.utm_content) url.searchParams.set('utm_content', form.utm_content)
    if (form.utm_term) url.searchParams.set('utm_term', form.utm_term)
    return url.toString()
  } catch {
    return (
      <Text as="span" color="gray.500">
        URL inválida
      </Text>
    )
  }
}

interface IUtmFieldProps {
  utmKey: keyof IListUtms
  value: string
  options: string[]
  isAddingUtm: boolean
  saving: boolean
  formError?: string
  onChange: (value: string) => void
  onToggleAdding: () => void
  onDelete: (value: string) => void
  used_by: 'marketing' | 'commercial'
}

interface IUtmFieldRef {
  focus: () => void
}

const UtmField = forwardRef<IUtmFieldRef, IUtmFieldProps>(
  ({ utmKey, value, options, isAddingUtm, saving, formError, onChange, onToggleAdding, onDelete, used_by }, ref) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const hasRole = useHasRole()
    const permissions = {
      manageUtms: used_by === 'commercial' ? hasRole('manage_utms') : hasRole('manage_marketing_utms')
    }

    useImperativeHandle(ref, () => ({
      focus: () => {
        if (inputRef.current) {
          inputRef.current.focus()
        }
      }
    }))

    return (
      <FormField label={utmKey.toUpperCase()} error={formError}>
        <Flex align="center" gap={2}>
          <Box flex={1}>
            {isAddingUtm ? (
              <Input
                ref={inputRef}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                disabled={saving}
                maxLength={255}
                placeholder={`Digite uma nova ${utmKey}`}
              />
            ) : (
              <SearchableSelect
                value={value}
                isDisabled={saving}
                options={options.map((optionValue: string) => ({ value: optionValue, label: optionValue }))}
                onChange={onChange}
                placeholder={`Selecione uma ${utmKey}`}
                entity={utmKey}
                feminine
                renderOption={
                  permissions.manageUtms
                    ? (option) => (
                        <Flex align="center" justify="space-between">
                          <Box>{option.label}</Box>
                          <IconButton
                            aria-label="Excluir UTM"
                            icon={<Icon name="lni lni-trash-can" />}
                            size="xs"
                            colorScheme="red"
                            onClick={(event) => {
                              event.stopPropagation()
                              onDelete(String(option.value))
                            }}
                          />
                        </Flex>
                      )
                    : undefined
                }
              />
            )}
          </Box>
          {permissions.manageUtms && (
            <IconButton
              aria-label={`Adicionar ${utmKey}`}
              icon={isAddingUtm ? <Icon name="lni lni-chevron-left" /> : <Icon name="lni lni-plus" />}
              variant="outline"
              border="1px solid"
              borderColor="gray.300"
              _hover={{ bg: 'gray.50', borderColor: 'gray.400' }}
              mr={2}
              onClick={onToggleAdding}
            />
          )}
        </Flex>
      </FormField>
    )
  }
)

UtmField.displayName = 'UtmField'

export default function UtmGeneratorFormModal({
  isOpen,
  onClose,
  onSaved,
  used_by,
  initialData,
  editing
}: IUtmGeneratorFormModalProps) {
  const toast = useAppToast()
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<ICreateUrlShortenerPayload>({
    title: '',
    original_url: '',
    short_path: '',
    utm_campaign: '',
    utm_source: '',
    utm_medium: '',
    utm_content: '',
    utm_term: '',
    used_by
  })
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [utmOptions, setUtmOptions] = useState<IListUtms>({
    utm_campaign: [],
    utm_source: [],
    utm_medium: [],
    utm_content: [],
    utm_term: []
  })
  const [isAddingUtm, setIsAddingUtm] = useState<(keyof IListUtms)[]>([])
  const [deletingUtm, setDeletingUtm] = useState<{
    value: string
    utmType: keyof IListUtms
  } | null>(null)

  // Refs para os componentes UtmField
  const utmFieldRefs = useRef<Record<keyof IListUtms, IUtmFieldRef | null>>({
    utm_campaign: null,
    utm_source: null,
    utm_medium: null,
    utm_content: null,
    utm_term: null
  })

  useEffect(() => {
    setIsAddingUtm([])
    if (editing) {
      // Modo edição - dados exatos do registro
      setForm({
        title: editing.title,
        original_url: editing.original_url,
        short_path: editing.short_path,
        utm_campaign: editing.utm_campaign || '',
        utm_source: editing.utm_source || '',
        utm_medium: editing.utm_medium || '',
        utm_content: editing.utm_content || '',
        utm_term: editing.utm_term || '',
        used_by
      })
    } else if (initialData) {
      // Modo cópia - pré-preencher com dados iniciais e adicionar sufixo "_copia"
      setForm({
        title: initialData.title ? `${initialData.title}_copia` : '',
        original_url: initialData.original_url || '',
        short_path: initialData.short_path ? `${initialData.short_path}_copia` : '',
        utm_campaign: initialData.utm_campaign || '',
        utm_source: initialData.utm_source || '',
        utm_medium: initialData.utm_medium || '',
        utm_content: initialData.utm_content || '',
        utm_term: initialData.utm_term || '',
        used_by
      })
    } else {
      // Modo criação normal - campos vazios
      setForm({
        title: '',
        original_url: '',
        short_path: '',
        utm_campaign: '',
        utm_source: '',
        utm_medium: '',
        utm_content: '',
        utm_term: '',
        used_by
      })
    }
    fetchUtms()
    setFormErrors({})
    setDeletingUtm(null)
  }, [isOpen, initialData, editing])

  const fetchUtms = async () => {
    try {
      const data = await UrlShortenerService.listUtms(used_by)
      setUtmOptions(data.data)
    } catch (e) {
      toast.error('Erro ao carregar UTMs', e)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    setFormErrors({})
    try {
      if (editing) {
        await UrlShortenerService.update(editing.id, form)
        toast.success('URL curta atualizada com sucesso')
      } else {
        await UrlShortenerService.create(form)
        toast.success('URL curta criada com sucesso')
      }
      setForm({
        title: '',
        original_url: '',
        short_path: '',
        utm_campaign: '',
        utm_source: '',
        utm_medium: '',
        utm_content: '',
        utm_term: '',
        used_by
      })
      onSaved()
      onClose()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      // Path duplicado volta 400 na criação e 422 na edição.
      const status = err.response?.status
      if (status === 400 || status === 422) setFormErrors(toFormErrors({}, err.response?.data?.errors))
      else toast.error(editing ? 'Erro ao atualizar URL' : 'Erro ao criar URL', err)
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteUtm = async () => {
    if (!deletingUtm) return
    try {
      await UrlShortenerService.deleteUtm(deletingUtm?.value, deletingUtm?.utmType, used_by)
      toast.success('UTM deletada com sucesso')
    } catch (error) {
      toast.error('Erro ao deletar UTM', error)
    }
    fetchUtms()
    setDeletingUtm(null)
  }

  const toggleAddingUtm = (utm: keyof IListUtms) => {
    setIsAddingUtm((prev) => {
      const isCurrentlyAdding = prev.includes(utm)
      if (isCurrentlyAdding) {
        return prev.filter((item) => item !== utm)
      } else {
        // Dar foco no input após o componente ser renderizado
        setTimeout(() => {
          if (utmFieldRefs.current[utm]) {
            utmFieldRefs.current[utm]?.focus()
          }
        }, 0)
        return [...prev, utm]
      }
    })
  }

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={onClose}
        title={editing ? 'Editar URL com UTM' : 'Criar URL com UTM'}
        size="wide"
        footer={
          <>
            <Button colorScheme="magenta" mr={3} onClick={handleSave} isLoading={saving}>
              {editing ? 'Salvar' : 'Criar'}
            </Button>
            <Button variant="ghost" onClick={onClose} disabled={saving}>
              Cancelar
            </Button>
          </>
        }
      >
        <Flex direction="row" gap={5}>
          <Flex direction="column" w="50%" gap={3}>
            <Box>
              <FormField label="Título" error={formErrors.title}>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  disabled={saving}
                  maxLength={255}
                />
              </FormField>
            </Box>
            <Box>
              <FormField label="URL de Destino" error={formErrors.original_url}>
                <Input
                  value={form.original_url}
                  onChange={(e) => setForm({ ...form, original_url: e.target.value })}
                  disabled={saving}
                  type="url"
                />
              </FormField>
            </Box>
            <Box>
              <FormField
                label="Path (opcional)"
                error={formErrors.short_path}
                helperText={
                  editing && form.short_path !== editing.short_path
                    ? 'Alterar o path desativa o link antigo e QR codes já divulgados.'
                    : undefined
                }
              >
                <Input
                  value={form.short_path}
                  onChange={(e) => setForm({ ...form, short_path: e.target.value })}
                  disabled={saving}
                  maxLength={255}
                />
              </FormField>
            </Box>
            <Divider />
            <Box>
              <FormField label="URL pré-pronta">
                <Text>{formatUrlWithUTMs(form)}</Text>
              </FormField>
            </Box>
          </Flex>
          <Flex direction="column" w="50%" gap={3}>
            {(['utm_campaign', 'utm_source', 'utm_medium', 'utm_content', 'utm_term'] as (keyof IListUtms)[]).map(
              (utmKey) => (
                <UtmField
                  key={utmKey}
                  ref={(el) => (utmFieldRefs.current[utmKey] = el)}
                  utmKey={utmKey}
                  value={form[utmKey] || ''}
                  options={utmOptions[utmKey] || []}
                  isAddingUtm={isAddingUtm.includes(utmKey)}
                  saving={saving}
                  formError={formErrors[utmKey]}
                  onChange={(value) => setForm({ ...form, [utmKey]: value })}
                  onToggleAdding={() => toggleAddingUtm(utmKey)}
                  onDelete={(value: string) => setDeletingUtm({ value, utmType: utmKey })}
                  used_by={used_by}
                />
              )
            )}
          </Flex>
        </Flex>
      </FormModal>
      <ConfirmDialog
        isOpen={deletingUtm != null}
        onClose={() => setDeletingUtm(null)}
        onConfirm={handleDeleteUtm}
        action="delete"
        entity="UTM"
        feminine
        warning="As urls curtas ligadas a ela também são afetadas."
      />
    </>
  )
}
